import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiGet } from '../api';
import { getDisplayError } from '../apiErrors';
import { getToken } from '../auth';

export type ImageQueueStatus = 'PENDING' | 'REVIEW' | 'FAILED';

export type ImageQueueStats = {
  pending: number;
  review: number;
  ready: number;
  failed: number;
};

export type ImageQueueRow = {
  id: string;
  slug: string;
  name: string;
  imageStatus: string;
  imageStatusReason: string | null;
  reviewCandidate?: {
    source: string;
    imageUrl: string;
    pageUrl: string | null;
    confidence: number;
  } | null;
};

type ImageQueueResponse = {
  items: ImageQueueRow[];
  total: number;
};

export function imageQueueActionCount(stats: ImageQueueStats | null): number {
  if (!stats) return 0;
  return stats.review + stats.pending + stats.failed;
}

/** Global PIIS queue (products outside an active resolve job card). */
export function useCatalogImageResolveQueue(status: ImageQueueStatus) {
  const token = getToken();
  const [stats, setStats] = useState<ImageQueueStats | null>(null);
  const [queue, setQueue] = useState<ImageQueueResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setErr(null);
    try {
      const [statsBody, queueBody] = await Promise.all([
        apiGet<ImageQueueStats>('/catalog/admin/image-intelligence/stats', token),
        apiGet<ImageQueueResponse>(
          `/catalog/admin/image-intelligence/queue?status=${status}&limit=50`,
          token,
        ),
      ]);
      setStats(statsBody);
      setQueue(queueBody);
    } catch (e: unknown) {
      setErr(getDisplayError(e));
    } finally {
      setLoading(false);
    }
  }, [status, token]);

  useEffect(() => {
    void load();
  }, [load]);

  const statusCounts = useMemo(
    () => ({
      PENDING: stats?.pending ?? 0,
      REVIEW: stats?.review ?? 0,
      FAILED: stats?.failed ?? 0,
    }),
    [stats],
  );

  return {
    stats,
    queue,
    loading,
    err,
    reload: load,
    actionCount: imageQueueActionCount(stats),
    statusCounts,
  };
}
